import { createContext, useContext, useReducer } from 'react'
import { chatService } from '../services/chatService'

const ChatContext = createContext()

const chatReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload, loading: false }
    case 'SET_CURRENT_CONVERSATION':
      return { ...state, currentConversation: action.payload, loading: false }
    case 'ADD_CONVERSATION':
      return { 
        ...state, 
        conversations: [action.payload, ...state.conversations],
        currentConversation: action.payload 
      }
    case 'UPDATE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv._id === action.payload._id ? action.payload : conv
        ),
        currentConversation: state.currentConversation?._id === action.payload._id 
          ? action.payload 
          : state.currentConversation
      }
    case 'DELETE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.filter(conv => conv._id !== action.payload),
        currentConversation: state.currentConversation?._id === action.payload 
          ? null 
          : state.currentConversation
      }
    case 'ADD_MESSAGE':
      if (state.currentConversation) {
        const updatedConversation = {
          ...state.currentConversation,
          messages: [...state.currentConversation.messages, action.payload]
        }
        return {
          ...state,
          currentConversation: updatedConversation
        }
      }
      return state
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

export const ChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, {
    conversations: [],
    currentConversation: null,
    loading: false,
    error: null
  })

  const loadConversations = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const conversations = await chatService.getConversations()
      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const loadConversation = async (conversationId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const conversation = await chatService.getConversation(conversationId)
      dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: conversation })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const createConversation = async (title, message) => {
    try {
      const conversation = await chatService.createConversation(title, message)
      dispatch({ type: 'ADD_CONVERSATION', payload: conversation })
      return conversation
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  const sendMessage = async (conversationId, message) => {
    try {
      // Add user message immediately
      dispatch({ 
        type: 'ADD_MESSAGE', 
        payload: { 
          role: 'user', 
          content: message, 
          timestamp: new Date().toISOString() 
        } 
      })

      const response = await chatService.sendMessage(conversationId, message)
      
      // Update the conversation with the response
      dispatch({ type: 'UPDATE_CONVERSATION', payload: response.conversation })
      
      return response.latestMessage
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  const deleteConversation = async (conversationId) => {
    try {
      await chatService.deleteConversation(conversationId)
      dispatch({ type: 'DELETE_CONVERSATION', payload: conversationId })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  const clearCurrentConversation = () => {
    dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: null })
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value = {
    conversations: state.conversations,
    currentConversation: state.currentConversation,
    loading: state.loading,
    error: state.error,
    loadConversations,
    loadConversation,
    createConversation,
    sendMessage,
    deleteConversation,
    clearCurrentConversation,
    clearError
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}

export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}
